import { apiClient } from './client';

// Feature value types
export type FeatureValueType = 'BOOLEAN' | 'INTEGER' | 'STRING_ARRAY' | 'DATE_RANGE_DAYS';

// Feature definition from database
export interface FeatureDefinition {
    id: string;
    key: string;
    name: string;
    description?: string;
    category: string;
    valueType: FeatureValueType;
    defaultValue: string; // JSON string: { "value": ... }
    validation?: string; // JSON string
    usageModel?: string;
    usageCountField?: string;
    uiConfig?: string; // JSON string
    createdAt: string;
    updatedAt: string;
}

// Feature definition with parsed values (for UI)
export interface ParsedFeatureDefinition {
    id: string;
    key: string;
    name: string;
    description?: string;
    category: string;
    type: FeatureValueType;
    defaultValue: any;
    validation?: { min?: number; max?: number; allowedValues?: string[] };
    usageModel?: string;
    usageCountField?: string;
    uiConfig?: { icon?: string; warningThreshold?: number };
}

// Keyed definitions from the definitions endpoint
export interface DefinitionsMap {
    [key: string]: {
        id: string;
        name: string;
        description?: string;
        type: FeatureValueType;
        category: string;
        defaultValue: any;
        validation?: any;
        usageModel?: string;
        usageCountField?: string;
        uiConfig?: any;
    };
}

// Product feature value
export interface ProductFeatureValue {
    id: string;
    value: any;
    featureId: string;
}

// Categories for grouping
export const FEATURE_CATEGORIES = [
    { key: 'board_explorer', label: 'Board Explorer', icon: 'layout-dashboard' },
    { key: 'board', label: 'Board Settings', icon: 'layout' },
    { key: 'charts', label: 'Charts', icon: 'bar-chart' },
    { key: 'templates', label: 'Templates', icon: 'layout-template' },
    { key: 'channels', label: 'Channels', icon: 'plug' },
    { key: 'journals', label: 'Journals', icon: 'book-open' },
    { key: 'sharing', label: 'Sharing', icon: 'share' },
    { key: 'other', label: 'Other', icon: 'settings' },
];

export const featuresApi = {
    // Get all feature definitions as a keyed map (for product config)
    getDefinitions: async (): Promise<DefinitionsMap> => {
        const res = await apiClient.get('/admin/features/definitions');
        return res.data.data;
    },

    // Get all feature definitions as a list (for feature registry)
    list: async (): Promise<FeatureDefinition[]> => {
        const res = await apiClient.get('/admin/features');
        return res.data.data;
    },

    // Get a single feature
    get: async (id: string): Promise<FeatureDefinition> => {
        const res = await apiClient.get(`/admin/features/${id}`);
        return res.data.data;
    },

    // Create a new feature definition
    create: async (data: {
        key: string;
        name: string;
        description?: string;
        category: string;
        valueType: FeatureValueType;
        defaultValue: any;
        validation?: any;
        usageModel?: string;
        usageCountField?: string;
        uiConfig?: any;
    }): Promise<FeatureDefinition> => {
        const res = await apiClient.post('/admin/features', data);
        return res.data.data;
    },

    // Update a feature definition
    update: async (id: string, data: Partial<{
        name: string;
        description?: string;
        category: string;
        valueType: FeatureValueType;
        defaultValue: any;
        validation?: any;
        usageModel?: string;
        usageCountField?: string;
        uiConfig?: any;
    }>): Promise<FeatureDefinition> => {
        const res = await apiClient.put(`/admin/features/${id}`, data);
        return res.data.data;
    },

    // Delete a feature definition
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/admin/features/${id}`);
    },

    // Get features for a product
    getProductFeatures: async (productId: string): Promise<{
        definitions: FeatureDefinition[];
        values: Record<string, ProductFeatureValue>;
    }> => {
        const res = await apiClient.get(`/admin/plans/${productId}/features`);
        return res.data.data;
    },

    // Set a single feature for a product
    setProductFeature: async (productId: string, featureKey: string, value: any, variantId?: string): Promise<any> => {
        const res = await apiClient.put(`/admin/plans/${productId}/features/${featureKey}`, {
            value,
            variantId
        });
        return res.data.data;
    },

    // Set multiple features for a product
    setProductFeatures: async (productId: string, features: Record<string, any>, variantId?: string): Promise<any> => {
        const res = await apiClient.put(`/admin/plans/${productId}/features`, {
            features,
            variantId
        });
        return res.data.data;
    },

    // Delete a feature from a product (revert to default)
    deleteProductFeature: async (productId: string, featureKey: string, variantId?: string): Promise<void> => {
        await apiClient.delete(`/admin/plans/${productId}/features/${featureKey}`, {
            params: { variantId }
        });
    },

    // Get features for a variant
    getVariantFeatures: async (variantId: string): Promise<{
        definitions: FeatureDefinition[];
        productValues: Record<string, any>;
        variantValues: Record<string, ProductFeatureValue>;
    }> => {
        const res = await apiClient.get(`/admin/plans/variants/${variantId}/features`);
        return res.data.data;
    },

    // Reset feature definitions to defaults
    resetDefaults: async (): Promise<void> => {
        await apiClient.post('/admin/features/reset-defaults');
    }
};
